import { AppointmentModel, AppointmentService } from "../fhir/AppointmentService";
import { ActorModel, AppointmentResource } from "../fhir/fhir.types";
import { ScheduleModel, ScheduleService } from "../fhir/ScheduleService";
import { SlotModel, SlotService } from "../fhir/SlotService";
import {
  ActorAvailability,
  AppointmentDraft,
  PlanningComparison,
  PlanningConflict,
  PlanningSuggestion,
  TimeInterval,
} from "./AppointmentPlanning.types";

function timestamp(value: string): number {
  return new Date(value).getTime();
}

function durationMs(interval: TimeInterval): number {
  return timestamp(interval.end) - timestamp(interval.start);
}

function overlaps(left: TimeInterval, right: TimeInterval): boolean {
  return timestamp(left.start) < timestamp(right.end) && timestamp(right.start) < timestamp(left.end);
}

function isBlockingAppointment(status: string): boolean {
  return !["cancelled", "noshow", "entered-in-error"].includes(status);
}

function isBlockingSlot(status: string): boolean {
  return status !== "free" && status !== "entered-in-error";
}

function mergeIntervals(intervals: TimeInterval[]): TimeInterval[] {
  const sorted = [...intervals].sort((a, b) => timestamp(a.start) - timestamp(b.start));
  const result: TimeInterval[] = [];
  sorted.forEach((interval) => {
    const previous = result[result.length - 1];
    if (!previous || timestamp(interval.start) > timestamp(previous.end)) {
      result.push({ ...interval });
      return;
    }
    if (timestamp(interval.end) > timestamp(previous.end)) {
      previous.end = interval.end;
    }
  });
  return result;
}

function intersectTwo(left: TimeInterval[], right: TimeInterval[]): TimeInterval[] {
  const intersections: TimeInterval[] = [];
  left.forEach((a) =>
    right.forEach((b) => {
      const start = Math.max(timestamp(a.start), timestamp(b.start));
      const end = Math.min(timestamp(a.end), timestamp(b.end));
      if (start < end) {
        intersections.push({
          start: new Date(start).toISOString(),
          end: new Date(end).toISOString(),
        });
      }
    }),
  );
  return mergeIntervals(intersections);
}

function scheduleLabels(schedules: ScheduleModel[]): Map<string, string> {
  return new Map(
    schedules.map((schedule) => [
      schedule.id,
      schedule.serviceLabels.join(" · ") || `Schedule/${schedule.id}`,
    ]),
  );
}

function conflictsForActor(
  actorReference: string,
  interval: TimeInterval,
  slotsByActor: Map<string, SlotModel[]>,
  appointmentsByActor: Map<string, AppointmentModel[]>,
): PlanningConflict[] {
  const slotConflicts: PlanningConflict[] = (slotsByActor.get(actorReference) ?? [])
    .filter((slot) => isBlockingSlot(slot.status) && overlaps(slot, interval))
    .map((slot) => ({
      kind: "slot",
      id: slot.id,
      label: `Slot/${slot.id}`,
      status: slot.status,
      start: slot.start,
      end: slot.end,
    }));

  const appointmentConflicts: PlanningConflict[] = (
    appointmentsByActor.get(actorReference) ?? []
  )
    .filter(
      (appointment) =>
        isBlockingAppointment(appointment.status) && overlaps(appointment, interval),
    )
    .map((appointment) => ({
      kind: "appointment",
      id: appointment.id,
      label: appointment.title,
      status: appointment.status,
      start: appointment.start,
      end: appointment.end,
    }));

  return [...slotConflicts, ...appointmentConflicts];
}

function suggestionForInterval(
  interval: TimeInterval,
  actors: ActorModel[],
  slotsByActor: Map<string, SlotModel[]>,
  appointmentsByActor: Map<string, AppointmentModel[]>,
): PlanningSuggestion {
  const compatibleSlotIdsByActor: Record<string, string[]> = {};
  const conflictsByActor: Record<string, PlanningConflict[]> = {};

  actors.forEach((actor) => {
    compatibleSlotIdsByActor[actor.reference] = (
      slotsByActor.get(actor.reference) ?? []
    )
      .filter(
        (slot) =>
          slot.status === "free" &&
          timestamp(slot.start) <= timestamp(interval.start) &&
          timestamp(slot.end) >= timestamp(interval.end),
      )
      .map((slot) => slot.id);

    conflictsByActor[actor.reference] = conflictsForActor(
      actor.reference,
      interval,
      slotsByActor,
      appointmentsByActor,
    );
  });

  return { ...interval, compatibleSlotIdsByActor, conflictsByActor };
}

function knownClearWindows(
  actors: ActorModel[],
  range: TimeInterval,
  slotsByActor: Map<string, SlotModel[]>,
  appointmentsByActor: Map<string, AppointmentModel[]>,
): TimeInterval[] {
  const clearByActor = actors.map((actor) => {
    const blockers: TimeInterval[] = [
      ...(slotsByActor.get(actor.reference) ?? [])
        .filter((slot) => isBlockingSlot(slot.status))
        .map((slot) => ({ start: slot.start, end: slot.end })),
      ...(appointmentsByActor.get(actor.reference) ?? [])
        .filter((appointment) => isBlockingAppointment(appointment.status))
        .map((appointment) => ({ start: appointment.start, end: appointment.end })),
    ];

    let windows: TimeInterval[] = [{ ...range }];
    blockers.forEach((blocker) => {
      windows = windows.flatMap((window) => {
        if (!overlaps(window, blocker)) {
          return [window];
        }
        const fragments: TimeInterval[] = [];
        if (timestamp(blocker.start) > timestamp(window.start)) {
          fragments.push({ start: window.start, end: blocker.start });
        }
        if (timestamp(blocker.end) < timestamp(window.end)) {
          fragments.push({ start: blocker.end, end: window.end });
        }
        return fragments;
      });
    });
    return windows;
  });

  return clearByActor.reduce<TimeInterval[]>(
    (common, actorWindows, index) =>
      index === 0 ? actorWindows : intersectTwo(common, actorWindows),
    [],
  );
}

function buildActorAvailability(
  actors: ActorModel[],
  schedules: ScheduleModel[],
  slotsByActor: Map<string, SlotModel[]>,
  appointmentsByActor: Map<string, AppointmentModel[]>,
): ActorAvailability[] {
  const labels = scheduleLabels(schedules);
  return actors.map((actor) => ({
    actor,
    slots: (slotsByActor.get(actor.reference) ?? []).map((slot) => ({
      id: slot.id,
      scheduleId: slot.scheduleId,
      scheduleLabel: labels.get(slot.scheduleId) ?? `Schedule/${slot.scheduleId}`,
      status: slot.status,
      start: slot.start,
      end: slot.end,
      overbooked: slot.overbooked,
    })),
    appointments: (appointmentsByActor.get(actor.reference) ?? []).map(
      (appointment) => ({
        id: appointment.id,
        title: appointment.title,
        status: appointment.status,
        start: appointment.start,
        end: appointment.end,
      }),
    ),
  }));
}

export class AppointmentPlanningService {
  constructor(
    private readonly scheduleService: ScheduleService,
    private readonly slotService: SlotService,
    private readonly appointmentService: AppointmentService,
  ) {}

  async compare(
    actors: ActorModel[],
    range: TimeInterval,
    durationMinutes: number,
  ): Promise<PlanningComparison> {
    if (actors.length === 0) {
      return { range, durationMinutes, actors: [], knownClear: [], suggestions: [] };
    }

    const schedules = await this.scheduleService.searchByActors(actors);
    const slots = await this.slotService.searchBySchedules(
      schedules.map((schedule) => schedule.id),
      range.start,
      range.end,
    );
    const appointments = await this.appointmentService.searchByActors(
      actors.map((actor) => actor.reference),
      range.start,
      range.end,
    );

    const actorBySchedule = new Map(
      schedules.map((schedule) => [schedule.id, schedule.actor.reference]),
    );
    const slotsByActor = new Map<string, SlotModel[]>();
    const appointmentsByActor = new Map<string, AppointmentModel[]>();
    actors.forEach((actor) => {
      slotsByActor.set(actor.reference, []);
      appointmentsByActor.set(actor.reference, []);
    });

    slots.forEach((slot) => {
      const actorReference = actorBySchedule.get(slot.scheduleId);
      if (actorReference) {
        slotsByActor.get(actorReference)?.push(slot);
      }
    });
    appointments.forEach((appointment) => {
      actors.forEach((actor) => {
        if (appointment.participantReferences.includes(actor.reference)) {
          appointmentsByActor.get(actor.reference)?.push(appointment);
        }
      });
    });

    const knownClear = knownClearWindows(
      actors,
      range,
      slotsByActor,
      appointmentsByActor,
    ).filter((interval) => durationMs(interval) >= durationMinutes * 60_000);

    const suggestions = knownClear.flatMap((window) => {
      const result: PlanningSuggestion[] = [];
      const duration = durationMinutes * 60_000;
      const step = 15 * 60_000;
      for (
        let start = timestamp(window.start);
        start + duration <= timestamp(window.end);
        start += step
      ) {
        result.push(
          suggestionForInterval(
            {
              start: new Date(start).toISOString(),
              end: new Date(start + duration).toISOString(),
            },
            actors,
            slotsByActor,
            appointmentsByActor,
          ),
        );
      }
      return result;
    });

    return {
      range,
      durationMinutes,
      actors: buildActorAvailability(
        actors,
        schedules,
        slotsByActor,
        appointmentsByActor,
      ),
      knownClear,
      suggestions,
    };
  }

  select(
    comparison: PlanningComparison,
    start: string,
  ): PlanningSuggestion {
    const startMs = timestamp(start);
    const endMs = startMs + comparison.durationMinutes * 60_000;
    const interval = {
      start: new Date(startMs).toISOString(),
      end: new Date(endMs).toISOString(),
    };

    if (
      startMs < timestamp(comparison.range.start) ||
      endMs > timestamp(comparison.range.end)
    ) {
      throw new Error("Selected period is outside the planning range.");
    }

    const slotsByActor = new Map<string, SlotModel[]>();
    const appointmentsByActor = new Map<string, AppointmentModel[]>();
    comparison.actors.forEach((availability) => {
      slotsByActor.set(
        availability.actor.reference,
        availability.slots.map((slot) => ({
          id: slot.id,
          scheduleId: slot.scheduleId,
          scheduleReference: `Schedule/${slot.scheduleId}`,
          status: slot.status,
          start: slot.start,
          end: slot.end,
          overbooked: slot.overbooked,
        })),
      );
      appointmentsByActor.set(
        availability.actor.reference,
        availability.appointments.map((appointment) => ({
          id: appointment.id,
          title: appointment.title,
          start: appointment.start,
          end: appointment.end,
          status: appointment.status,
          slotReferences: [],
          participantReferences: [availability.actor.reference],
          participantDisplays: [availability.actor.display],
        })),
      );
    });

    return suggestionForInterval(
      interval,
      comparison.actors.map((availability) => availability.actor),
      slotsByActor,
      appointmentsByActor,
    );
  }

  async create(draft: AppointmentDraft): Promise<void> {
    if (!draft.selected) {
      throw new Error("A planning period must be selected before creation.");
    }

    const selectedSlotIds = Object.values(draft.selectedSlotIdByActor).filter(
      (id): id is string => Boolean(id),
    );
    const slotResources = await this.slotService.readResources(selectedSlotIds);

    for (const slot of slotResources) {
      if (
        slot.status !== "free" ||
        !slot.start ||
        !slot.end ||
        timestamp(slot.start) > timestamp(draft.selected.start) ||
        timestamp(slot.end) < timestamp(draft.selected.end)
      ) {
        throw new Error(
          `Slot/${slot.id ?? "unknown"} is no longer available.`,
        );
      }
    }

    const appointment: AppointmentResource = {
      resourceType: "Appointment",
      status: "booked",
      description: draft.title.trim() || "Appointment",
      start: draft.selected.start,
      end: draft.selected.end,
      minutesDuration: draft.durationMinutes,
      slot: selectedSlotIds.map((id) => ({ reference: `Slot/${id}` })),
      participant: draft.actors.map((actor) => ({
        actor: { reference: actor.reference, display: actor.display },
        required: "required",
        status: "accepted",
      })),
      comment: draft.comment?.trim() || undefined,
    };

    await this.appointmentService.createWithReservedSlots(
      appointment,
      slotResources,
    );
  }
}
